import React, { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, X, Save, ImagePlus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { listNews, saveNews, deleteNews } from "../firebase";

const EMPTY = {
  slug: "",
  title: "",
  dek: "",
  category: "Announcement",
  date: new Date().toISOString().slice(0, 10),
  readTime: "2 min read",
  cover: "",
  body: "",
};

function slugify(s) {
  return s
    .toLowerCase()
    .trim()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export default function AdminNewsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null = list view, {} = create, {existing} = edit

  const load = async () => {
    setLoading(true);
    try {
      const data = await listNews(100);
      setRows(data);
    } catch (err) {
      toast.error("Couldn't load news", { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onSaved = async () => {
    setEditing(null);
    await load();
  };

  if (editing !== null) {
    return (
      <ArticleEditor
        initial={editing}
        onCancel={() => setEditing(null)}
        onSaved={onSaved}
      />
    );
  }

  return (
    <div data-testid="admin-news-panel" className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-ink/65">
          {rows.length} article{rows.length === 1 ? "" : "s"} published
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            data-testid="admin-news-refresh"
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs text-muted hover:text-coral"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            onClick={() => setEditing({ ...EMPTY })}
            data-testid="admin-news-new"
            className="inline-flex items-center gap-2 rounded-full bg-ink text-bone px-5 py-2.5 text-sm font-medium hover:bg-coral transition-colors"
          >
            <Plus className="h-4 w-4" /> New article
          </button>
        </div>
      </div>

      {rows.length === 0 && !loading ? (
        <div className="text-center py-16 text-muted border border-dashed border-ink/15 rounded-3xl">
          <p>No articles in Firestore yet.</p>
          <p className="mt-2 text-xs">Public site is showing seeded stories. Create your first article above.</p>
        </div>
      ) : (
        <ul className="rounded-3xl border border-ink/10 bg-boneSoft divide-y divide-ink/5">
          {rows.map((r) => (
            <li
              key={r.id}
              data-testid={`admin-news-row-${r.slug}`}
              className="p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-5"
            >
              <div className="h-16 w-24 md:h-14 md:w-20 rounded-xl bg-bone overflow-hidden flex-shrink-0">
                {r.cover ? (
                  <img src={r.cover} alt={r.title} className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full grid place-items-center text-muted">
                    <ImagePlus className="h-5 w-5" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] uppercase tracking-[0.22em] text-coral">{r.category}</p>
                <p className="mt-1 font-display text-[18px] text-ink leading-snug truncate">{r.title}</p>
                <p className="mt-1 text-xs text-muted">
                  {r.date} · /news/{r.slug}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setEditing({ ...r, body: Array.isArray(r.body) ? r.body.join("\n\n") : r.body || "" })}
                  data-testid={`admin-news-edit-${r.slug}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 hover:border-coral hover:text-coral px-4 py-2 text-xs"
                >
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
                <button
                  onClick={async () => {
                    if (!window.confirm(`Delete "${r.title}"?`)) return;
                    try {
                      await deleteNews(r.id);
                      toast.success("Article deleted");
                      await load();
                    } catch (err) {
                      toast.error("Delete failed", { description: err.message });
                    }
                  }}
                  data-testid={`admin-news-delete-${r.slug}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 hover:border-coral hover:text-coral px-3 py-2 text-xs"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ArticleEditor({ initial, onCancel, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const isNew = !initial.id;

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  // Auto-slug from title when creating a new article.
  useEffect(() => {
    if (isNew) set("slug", slugify(form.title));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.title]);

  const wordCount = useMemo(
    () => (form.body || "").trim().split(/\s+/).filter(Boolean).length,
    [form.body]
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.slug.trim()) return toast.error("Slug is required");
    if (!form.body.trim()) return toast.error("Article body is required");

    setSaving(true);
    try {
      await saveNews({
        slug: form.slug,
        title: form.title.trim(),
        dek: form.dek.trim(),
        category: form.category.trim() || "Announcement",
        date: form.date,
        readTime: form.readTime || "2 min read",
        cover: form.cover.trim(),
        body: form.body.trim(),
      });
      toast.success(isNew ? "Article published" : "Article updated");
      onSaved();
    } catch (err) {
      toast.error("Save failed", { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const saveLabel = (() => {
    if (saving) return "Saving…";
    if (isNew) return "Publish article";
    return "Save changes";
  })();

  return (
    <form
      onSubmit={submit}
      data-testid="admin-news-editor"
      className="rounded-3xl border border-ink/10 bg-boneSoft p-6 md:p-8 space-y-6"
    >
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-display text-[24px] text-ink tracking-tightest">
          {isNew ? "New article" : "Edit article"}
        </h3>
        <button
          type="button"
          onClick={onCancel}
          data-testid="admin-news-editor-cancel"
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs text-muted hover:text-coral border border-ink/10 hover:border-coral"
        >
          <X className="h-3.5 w-3.5" /> Cancel
        </button>
      </div>

      <div className="grid md:grid-cols-12 gap-5">
        <Field label="Title *" className="md:col-span-8">
          <input
            data-testid="admin-news-title"
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            placeholder="A short, punchy title"
            className="input"
          />
        </Field>
        <Field label="Category" className="md:col-span-4">
          <input
            data-testid="admin-news-category"
            value={form.category}
            onChange={(e) => set("category", e.target.value)}
            placeholder="Announcement"
            className="input"
          />
        </Field>

        <Field label="Slug (URL) *" className="md:col-span-6">
          <input
            data-testid="admin-news-slug"
            value={form.slug}
            onChange={(e) => set("slug", slugify(e.target.value))}
            placeholder="my-article-slug"
            className="input font-mono text-sm"
          />
        </Field>
        <Field label="Publish date" className="md:col-span-3">
          <input
            type="date"
            data-testid="admin-news-date"
            value={form.date}
            onChange={(e) => set("date", e.target.value)}
            className="input"
          />
        </Field>
        <Field label="Read time" className="md:col-span-3">
          <input
            data-testid="admin-news-readtime"
            value={form.readTime}
            onChange={(e) => set("readTime", e.target.value)}
            placeholder="2 min read"
            className="input"
          />
        </Field>

        <Field label="Subtitle / dek" className="md:col-span-12">
          <input
            data-testid="admin-news-dek"
            value={form.dek}
            onChange={(e) => set("dek", e.target.value)}
            placeholder="One-line summary shown under the title"
            className="input"
          />
        </Field>

        <Field label="Cover image URL" className="md:col-span-12">
          <input
            data-testid="admin-news-cover"
            value={form.cover}
            onChange={(e) => set("cover", e.target.value)}
            placeholder="https://… (1600×900 looks best)"
            className="input font-mono text-sm"
          />
          {form.cover && (
            <div className="mt-3 h-40 rounded-2xl overflow-hidden border border-ink/10">
              <img src={form.cover} alt="cover preview" className="h-full w-full object-cover" />
            </div>
          )}
        </Field>

        <Field
          label={`Article body * — ${wordCount} word${wordCount === 1 ? "" : "s"}`}
          hint="Plain text. Separate paragraphs with a blank line."
          className="md:col-span-12"
        >
          <textarea
            data-testid="admin-news-body"
            value={form.body}
            onChange={(e) => set("body", e.target.value)}
            rows={12}
            placeholder={"First paragraph.\n\nSecond paragraph.\n\nThird paragraph."}
            className="input font-sans leading-relaxed"
          />
        </Field>
      </div>

      <div className="flex items-center justify-end gap-3 pt-3 border-t border-ink/5">
        <button
          type="button"
          onClick={onCancel}
          className="px-5 py-2.5 text-sm text-ink/70 hover:text-coral"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          data-testid="admin-news-save"
          className="inline-flex items-center gap-2 rounded-full bg-coral text-white px-6 py-3 text-sm font-medium hover:bg-coralDeep disabled:opacity-60 transition-colors"
        >
          <Save className="h-4 w-4" />
          {saveLabel}
        </button>
      </div>

      <style>{`
        .input {
          width: 100%;
          background: #F8F4EC;
          border: 1px solid rgba(31,27,22,0.12);
          border-radius: 14px;
          padding: 12px 14px;
          font-size: 14.5px;
          color: #1F1B16;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .input:focus {
          outline: none;
          border-color: #E07856;
          box-shadow: 0 0 0 3px rgba(224,120,86,0.15);
        }
      `}</style>
    </form>
  );
}

function Field({ label, hint, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[11px] uppercase tracking-[0.22em] text-muted mb-2">
        {label}
      </span>
      {children}
      {hint && <span className="block text-[11px] text-muted mt-1.5">{hint}</span>}
    </label>
  );
}
